import _ from 'lodash';
import * as actionTypes from 'constants/action-types';
import { MockServiceActionTypes } from 'store/mockServices/mockServiceActionTypes';
import { ActionCreator, AnyAction } from 'redux';
import { ThunkAction, ThunkDispatch } from 'redux-thunk';
import { ApplicationState } from 'store/rootReducer';
import { showToastNotification } from 'store/ui/toasts/toastsActions';
import { ToastType } from 'store/ui/toasts/toastsTypes';
import axios from 'axios/axios-base';
import MixpanelService from 'utils/mixpanelService';
import { CREATE_MOCK_SERVICE, FILTER_MOCK_SERVICES } from 'utils/mixpanelEvents';
import { generateDate } from 'utils/dateUtil';
import { applySearchTermFilterToList } from 'utils/filterUtils';
import { getDefaultService } from 'store/ui/service/serviceActions';
import i18n from 'i18n';
import {
  MESSAGING,
  MessagingProtocol,
  MockService,
  PriorityMode,
  RecorderStatus,
  TRANSACTIONAL,
} from 'types/rest/mockServiceTypes';
import {
  resetTransactionShuttle,
  setAssetBundle,
  updateHttpPollingStatus,
  updateHttpRecordedMessages,
  updatePollingStatus,
  updateRecordedMessages,
  updateRunnerTab,
} from 'store/mockServices/mockServiceDetail/mockServiceDetailActions';
import { CraneLocation, LOCATION_MOCK_SERVICES } from 'types/rest/locationTypes';
import { formattedTextMulti } from '../../utils/commonUtil';
import { action } from 'typesafe-actions';
import { cacheControlHeaders, getErrorMessage } from '../../utils/miscUtil';
import { PROVIDER_CLOUD, PROVIDER_OPL } from '../../constants/bzm-constants';
import { Filter } from './mockServiceTypes';
import acApi from '../../axios/ac/acApi';
import { mockServiceChanged } from './mockServiceList/mockServiceListActions';
import { delay } from 'q';
import { BlazeMeterApiResponse } from '../../types/rest/blazeMeterApiResponse';
import analyticsApi from '../../axios/ac/analyticsApi';

export const createMockServiceThunk: ActionCreator<ThunkAction<any, ApplicationState, null, MockServiceActionTypes>> = (
  type?: string,
  mockServiceTransactions?: any,
  oldServiceName?: string,
  newServiceName?: string,
): ThunkAction<any, ApplicationState, null, MockServiceActionTypes> => {
  return async (dispatch: ThunkDispatch<ApplicationState, any, AnyAction>, getState: any) => {
    try {
      if (!oldServiceName && !newServiceName) {
        dispatch(
          showToastNotification(
            ToastType.Info,
            i18n.t('mockServices:infoMsgs.createMockServiceHeading'),
            i18n.t('mockServices:infoMsgs.createMockServiceStart'),
          ),
        );
      }

      const newMockServiceName = 'MockService-' + generateDate();
      const newService = determineService(getState, dispatch);
      const predefinedLocation = determineLocation(getState());

      let msData;

      if (type === TRANSACTIONAL) {
        msData = {
          id: null,
          serviceId: newService.id,
          name: newMockServiceName,
          description: '',
          harborId: predefinedLocation.harborId,
          shipId: predefinedLocation.shipId,
          templateId: 0,
          endpointPreference: 'HTTPS',
          transactionIds: [],
          noMatchingRequestPreference: 'return404',
          thinkTime: 0,
          replicas: 1,
          liveSystemHost: '',
          liveSystemPort: -1,
          type: TRANSACTIONAL,
          priorityMode: PriorityMode.default,
          mockServiceTransactions: mockServiceTransactions,
          httpRunnerEnabled: true,
          messagingRunnerEnabled: false,
          httpRunnerOverrideKeystore: true,
          httpRunnerOverrideTruststore: true,
        };
      } else if (type === MESSAGING) {
        msData = {
          serviceId: newService.id,
          name: newMockServiceName,
          description: '',
          harborId: null,
          shipId: null,
          thinkTime: 100,
          capacity: 1,
          replicas: 1,
          type: TRANSACTIONAL,
          groupTag: '',
          httpRunnerEnabled: false,
          messagingRunnerEnabled: true,
          messagingProtocol: MessagingProtocol.IBM_MQ9_JMS,
          httpRunnerOverrideKeystore: true,
          httpRunnerOverrideTruststore: true,
        };
      } else {
        dispatch(showToastNotification(ToastType.Danger, 'unsupported virtual service type'));
        return;
      }

      MixpanelService.track(CREATE_MOCK_SERVICE(type), true);

      // Create draft at first place
      const createResponse = await axios.post(`/workspaces/{workspaceId}/service-mock-drafts`, msData);

      // code for PC, to show same ServiceName as it has in the ServicePicker
      createResponse.data.result.serviceName = newService.name;
      const currentSearchTerm = getState().ui.pageFilter.searchTerm;
      const filteredNewItem = applySearchTermFilterToList([createResponse.data.result], currentSearchTerm);
      const showNewItem = filteredNewItem.length > 0;

      //Set draft and isNew flag to true, because this is not done by draft api
      const createdDraft = { ...createResponse.data.result, isDraft: true, isNew: true, hits: '-' };

      if (showNewItem) {
        if (oldServiceName && newServiceName) {
          dispatch(
            showToastNotification(
              ToastType.Success,
              '',
              formattedTextMulti(
                i18n.t('mockServices:successMsgs.createServiceMsg'),
                i18n.t('mockServices:successMsgs.createServiceClonedMsg'),
                i18n.t('mockServices:successMsgs.mockServiceMsg'),
                i18n.t('mockServices:successMsgs.mockServiceCreateMsg'),
                oldServiceName,
                newServiceName,
                newMockServiceName,
              ),
            ),
          );
        } else {
          dispatch(
            showToastNotification(
              ToastType.Success,
              i18n.t('mockServices:successMsgs.createSuccessMsg', { name: newMockServiceName }),
            ),
          );
        }
      } else {
        dispatch(
          showToastNotification(
            ToastType.Warning,
            i18n.t('mockServices:successMsgs.createSuccessMsg'),
            i18n.t('mockServices:warnMsgs.createMockServiceButFilteredOut', { name: newMockServiceName }),
          ),
        );
      }
      // Reset Virtual Service detail
      dispatch(resetTransactionShuttle());
      dispatch(setAssetBundle(null));

      dispatch({
        type: actionTypes.CREATE_MOCK_SERVICE,
        payload: {
          newMockService: createdDraft,
          showNewItem: showNewItem,
        },
      });
    } catch (err) {
      dispatch(
        showToastNotification(
          ToastType.Danger,
          i18n.t('errorMsgs.networkProblem'),
          i18n.t('mockServices:errorMsgs.failedToCreateMockService'),
        ),
      );
    } finally {
      //After create set active tab to http runner
      dispatch(updateRunnerTab('httpRunner'));
    }
  };
};

export const updateIsMockServiceInWorkspace = (isMockServiceInWorkspace: boolean) =>
  action(actionTypes.UPDATE_IS_MS_IN_WORKSPACE, isMockServiceInWorkspace);

/**
 * Checks if there is any virtual service across service and saves a result to boolean.
 */
export const checkIfMockServiceExistInWorkspaceThunk: ActionCreator<any> = () => {
  return async (dispatch: ThunkDispatch<ApplicationState, any, AnyAction>) => {
    const response = (
      await axios.get(`/workspaces/{workspaceId}/ui/service-mocks`, {
        headers: cacheControlHeaders,
        params: {
          limit: 1,
        },
      })
    ).data;
    //TODO maybe and endpoint that returns true or false would be better???
    const isMsInWorkspace = response.result.length > 0;
    dispatch(updateIsMockServiceInWorkspace(isMsInWorkspace));
  };
};

/**
 * Collapses the MockServices.
 */
export const collapseAllMockServices = () => action(actionTypes.MOCK_SERVICE_COLLAPSE_ALL);

export const bulkMockServiceStageUpdate = (
  stages: {
    id: number | string;
    stage: string;
  }[],
) => action(actionTypes.BULK_STAGE_UPDATE, stages);

/** Update Virtual Service Filter */
export const updateMockServiceFilter = (filter: Filter) => action(actionTypes.MOCK_SERVICE_UPDATE_FILTER, filter);

export const determineService = (getState: any, dispatch: any) => {
  return dispatch(getDefaultService(true));
};

export const filterMockServices = (filterText = ''): MockServiceActionTypes => {
  MixpanelService.track(FILTER_MOCK_SERVICES(filterText));
  return {
    type: actionTypes.FILTER_MOCK_SERVICES,
    payload: filterText,
  };
};

export type BlazeLocation = {
  harborId: string | null;
  shipId: string | null;
};

/**
 * Stop recording for the selected virtual service.
 * @param mockService
 */
export const stopRecordingThunk: ActionCreator<ThunkAction<any, ApplicationState, null, MockServiceActionTypes>> = (
  mockService: MockService,
  type?: 'HTTP' | 'MESSAGING',
): ThunkAction<any, ApplicationState, null, MockServiceActionTypes> => {
  return async (dispatch: ThunkDispatch<ApplicationState, any, AnyAction>) => {
    const msId = mockService.isDraft ? mockService.serviceMockId : mockService.id;
    try {
      await acApi.mockServices.stopRecording(msId, { runnerType: type ? type : 'MESSAGING' });
      let update: MockService;
      if (type === 'HTTP') {
        update = { ...mockService, httpRecorderStatus: RecorderStatus.STOPPED };
      } else {
        update = { ...mockService, recorderStatus: RecorderStatus.STOPPED };
      }
      dispatch(mockServiceChanged(update));

      dispatch(
        showToastNotification(ToastType.Success, 'Recording stopped', 'Recording has been stopped successfully'),
      );
    } catch (error) {
      const msg = getErrorMessage(error);
      dispatch(showToastNotification(ToastType.Danger, 'Cannot start recording', msg));
    } finally {
      //Save polling status
      if (type === 'HTTP') {
        dispatch(updateHttpPollingStatus(false));
      } else {
        dispatch(updatePollingStatus(false));
      }
    }
  };
};

/**
 * Start recording for the selected virtual service.
 * @param mockService
 */
export const startRecordingThunk: ActionCreator<ThunkAction<any, ApplicationState, null, MockServiceActionTypes>> = (
  mockService: MockService,
  type?: 'HTTP' | 'MESSAGING',
): ThunkAction<any, ApplicationState, null, MockServiceActionTypes> => {
  return async (dispatch: ThunkDispatch<ApplicationState, any, AnyAction>, getState: any) => {
    const msId = mockService.isDraft ? mockService.serviceMockId : mockService.id;
    try {
      await acApi.mockServices.startRecording(msId, { runnerType: type ? type : 'MESSAGING' });

      let update: MockService;
      if (type === 'HTTP') {
        update = { ...mockService, httpRecorderStatus: RecorderStatus.RUNNING };
      } else {
        update = { ...mockService, recorderStatus: RecorderStatus.RUNNING };
      }
      dispatch(mockServiceChanged(update));

      dispatch(
        showToastNotification(ToastType.Success, 'Recording started', 'Recording has been started successfully'),
      );

      // Start polling
      dispatch(pollForNewRecordingMessages(mockService, type === 'HTTP' ? 'MOCK_REQUEST' : 'MESSAGING'));
    } catch (error) {
      const msg = getErrorMessage(error);
      dispatch(showToastNotification(ToastType.Danger, 'Cannot start recording', msg));
    }
  };
};

/**
 * Polls for new messages for the selected virtual service.
 * @param mockService
 */
export const pollForNewRecordingMessages: ActionCreator<ThunkAction<
  any,
  ApplicationState,
  null,
  MockServiceActionTypes
>> = (
  mockService: MockService,
  dataType?: string,
): ThunkAction<any, ApplicationState, null, MockServiceActionTypes> => {
  return async (dispatch: ThunkDispatch<ApplicationState, any, AnyAction>, getState: any) => {
    const mockId = mockService.isDraft ? mockService.serviceMockId : mockService.id;

    let total = await getTotalForMessages(mockId, dataType);
    const batchSize = 100; //TODO - change later according backend

    //Save polling status
    if (dataType === 'MOCK_REQUEST') {
      dispatch(updateHttpPollingStatus(true));
    } else {
      dispatch(updatePollingStatus(true));
    }

    while (true) {
      const ms: MockService = _.find(getState().mockservices.dataList, { id: mockService.id });
      const isExpanded = getState().mockservices.expandedMockServiceId === mockService.id;

      if (dataType === 'MOCK_REQUEST') {
        if (!ms || ms.httpRecorderStatus !== RecorderStatus.RUNNING || !isExpanded) {
          console.log('Stopping polling, recording is either not running anymore or mock service is not expanded');
          dispatch(updateHttpPollingStatus(false));
          break;
        }
      } else {
        if (!ms || ms.recorderStatus !== RecorderStatus.RUNNING || !isExpanded) {
          console.log('Stopping polling, recording is either not running anymore or mock service is not expanded');
          dispatch(updatePollingStatus(false));
          break;
        }
      }

      const response: BlazeMeterApiResponse = (
        await analyticsApi.recordings.getRecordingByServiceMock(mockId, {
          limit: batchSize,
          skip: total,
          sort: 'id',
          dataType: dataType ? dataType : undefined,
        })
      ).data;

      if (response) {
        const newItems = response.result;

        if (response.total === total) {
          console.log('Polling... No new messages');
        } else {
          console.log('Polling... New messages have arrived, fetching them.');
          total = response.total;

          if (dataType === 'MOCK_REQUEST') {
            const existingList = getState().mockServiceDetails.httpRecordings.messages;
            dispatch(updateHttpRecordedMessages([...existingList, ...newItems]));
          } else {
            const existingList = getState().mockServiceDetails.recordings.messages;
            dispatch(updateRecordedMessages([...existingList, ...newItems]));
          }
        }
      }

      await delay(3000);
    }
  };
};

const getTotalForMessages = async (mockServiceId: number, dataType?: string) => {
  const response: BlazeMeterApiResponse = (
    await analyticsApi.recordings.getRecordingByServiceMock(mockServiceId, {
      limit: 1,
      skip: 1,
      dataType: dataType ? dataType : undefined,
    })
  ).data;

  return response.total;
};

/**
 * Loads existing recorded messages for the mock service.
 * @param mockService
 */
export const getExistingRecordingMessages: ActionCreator<ThunkAction<
  Promise<void>,
  ApplicationState,
  null,
  MockServiceActionTypes
>> = (
  mockService: MockService,
  dataType?: string,
): ThunkAction<Promise<void>, ApplicationState, null, MockServiceActionTypes> => {
  return async (dispatch: ThunkDispatch<ApplicationState, any, AnyAction>): Promise<void> => {
    //If we have new, never deployed mock service, do not continue
    if (mockService.isNew) {
      return;
    }

    const msId = mockService.isDraft ? mockService.serviceMockId : mockService.id;
    const params = { limit: -1, skip: 0, sort: 'id', dataType: dataType ? dataType : undefined };
    try {
      const messages = (await analyticsApi.recordings.getRecordingByServiceMock(msId, params)).data;
      if (dataType === 'MOCK_REQUEST') {
        dispatch(updateHttpRecordedMessages(messages.result));
      } else {
        dispatch(updateRecordedMessages(messages.result));
      }
    } catch (e) {
      const message = getErrorMessage(e);
      dispatch(showToastNotification(ToastType.Danger, 'Cannot load recorded messages', message));
    }
  };
};

/**
 * Delete recordings by ids for the mock service.
 * @param mockService
 * @param analyticIds
 * @param dataType
 */
export const deleteRecordingsByIds: ActionCreator<ThunkAction<
  Promise<void>,
  ApplicationState,
  null,
  MockServiceActionTypes
>> = (
  mockService: MockService,
  analyticIds: any[],
  dataType?: string,
): ThunkAction<Promise<void>, ApplicationState, null, MockServiceActionTypes> => {
  return async (dispatch: ThunkDispatch<ApplicationState, any, AnyAction>, getState: any): Promise<void> => {
    const params = { limit: -1, skip: 0, sort: 'id', dataType: dataType ? dataType : undefined };
    try {
      const response = (await analyticsApi.recordings.deleteAnalyticById(mockService.id, analyticIds, params)).data;
      if (dataType === 'MOCK_REQUEST') {
        const updateHttp = _.filter(
          getState().mockServiceDetails.httpRecordings.messages,
          message => !_.includes(analyticIds, message.id),
        );
        dispatch(updateHttpRecordedMessages(updateHttp));
      } else {
        const updateMsg = _.filter(
          getState().mockServiceDetails.recordings.messages,
          message => !_.includes(analyticIds, message.id),
        );
        dispatch(updateRecordedMessages(updateMsg));
      }
    } catch (e) {
      const message = getErrorMessage(e);
      dispatch(showToastNotification(ToastType.Danger, 'Cannot delete recordings', message));
    }
  };
};

/**
 * In case we are in private cloud, try to preselect location for the blank virtual service from the
 * available locations - take the first one available.
 * @param state
 */
export const determineLocation = (state: ApplicationState): BlazeLocation => {
  try {
    // If private data is on, cloud is not available, preselect any location
    const locations: CraneLocation[] = state.mockservices.locData;
    if (!_.isEmpty(locations)) {
      //Try to find US East location first and return it if found
      const usEastLocationIndex = locations.findIndex((item: CraneLocation) => item.shipName.includes('US East'));
      if (usEastLocationIndex !== -1) {
        return {
          harborId: locations[usEastLocationIndex].harborId,
          shipId: locations[usEastLocationIndex].shipId,
        };
      }

      //If US East location is not available, find any location which supports transactional virtual services (PC)
      const anyTxnLocation = locations.find(
        loc =>
          loc.provider.toLowerCase() === PROVIDER_CLOUD.toLowerCase() ||
          (loc.provider.toLowerCase() === PROVIDER_OPL.toLowerCase() &&
            loc.funcIds.find(fc => fc === LOCATION_MOCK_SERVICES)),
      );

      if (anyTxnLocation !== undefined) {
        return { harborId: anyTxnLocation.harborId, shipId: anyTxnLocation.shipId };
      }
    }
  } catch (error) {
    console.error('Cannot determine default location', error);
  }

  return {
    harborId: null,
    shipId: null,
  };
};
